import { z } from 'zod';

/**
 * Transporte — **Minimalmodul** (ADR 0079). Ein Transport hält fest, dass Ware
 * kommt oder geht, woran sie hängt (Projekt bei ausgehend, Bestellungen bei
 * eingehend) und wann sie da ist.
 *
 * Geparkt, nicht gelöscht: LKWs, Container, Colli-Verladung, Gewichts-/Maßsummen,
 * Verladenachweis-Fotos und die Ladeliste (ADR 0066/0071). Die Tabellen dazu
 * stehen unverändert in der Datenbank, der Code liegt in der Git-Historie.
 */

/**
 * Status. Der Wertebereich bleibt vollständig, weil Bestandsdaten `ENTWURF` und
 * `VERLADEN` enthalten und in der Datenbank ein Enum steht — ein kleinerer Satz
 * wäre eine destruktive Migration. Neu angelegt wird nur noch `GEPLANT`; siehe
 * {@link TRANSPORT_SELECTABLE_STATUSES}.
 */
export const transportStatusSchema = z.enum([
  'ENTWURF',
  'GEPLANT',
  'VERLADEN',
  'ABGEFAHREN',
  'STORNIERT',
]);
export type TransportStatus = z.infer<typeof transportStatusSchema>;

/**
 * Richtung — der eine Unterschied, der alles andere bestimmt: ausgehend hängt an
 * einem Projekt, eingehend an Bestellungen. Wird bei Anlage gesetzt und bleibt
 * (keine Umwandlung — ein neuer Transport ist billiger als die Sonderfälle).
 */
export const transportDirectionSchema = z.enum(['OUTBOUND', 'INBOUND']);
export type TransportDirection = z.infer<typeof transportDirectionSchema>;

/**
 * Status, die im Minimalmodul aktiv gesetzt werden. `ENTWURF`/`VERLADEN` sind
 * Altlasten und werden angezeigt, aber nicht mehr vergeben.
 */
export const TRANSPORT_SELECTABLE_STATUSES: readonly TransportStatus[] = [
  'GEPLANT',
  'ABGEFAHREN',
  'STORNIERT',
];

/** Endzustände: hier ist nichts mehr zu tun. */
export const TRANSPORT_TERMINAL_STATUSES: readonly TransportStatus[] = ['ABGEFAHREN', 'STORNIERT'];

export function isTransportOpen(status: TransportStatus): boolean {
  return !TRANSPORT_TERMINAL_STATUSES.includes(status);
}

/**
 * Erlaubte Status-Übergänge. `ENTWURF` und `VERLADEN` haben nur noch Auswege —
 * Bestandsdaten sollen sich abschließen lassen, aber nicht neu dort landen.
 */
export const TRANSPORT_STATUS_TRANSITIONS: Readonly<Record<TransportStatus, readonly TransportStatus[]>> = {
  ENTWURF: ['GEPLANT', 'STORNIERT'],
  GEPLANT: ['ABGEFAHREN', 'STORNIERT'],
  VERLADEN: ['ABGEFAHREN', 'STORNIERT'],
  ABGEFAHREN: [],
  STORNIERT: [],
};

const freeText = (max: number) => z.string().trim().max(max).nullable().optional();

// ── Eingaben ─────────────────────────────────────────────────────────────────

/**
 * Gemeinsame Felder von Anlegen und Ändern. `plannedAt` ist der Termin: Ankunft
 * bei eingehend, Abholung bei ausgehend — Datum UND Uhrzeit, UTC wie überall
 * (Anzeige lokal).
 */
const transportFields = {
  projectId: z.string().uuid().nullable().optional(),
  purchaseOrderIds: z.array(z.string().uuid()).max(100).optional(),
  locationText: freeText(500),
  plannedAt: z.string().datetime().nullable().optional(),
  notes: freeText(2000),
};

/**
 * Transport anlegen — Nummer erzeugt der Server (`TA-`/`TE-JJlfd` je Richtung).
 *
 * Die Verknüpfung muss zur Richtung passen: ausgehend braucht ein Projekt,
 * eingehend nimmt Bestellungen. Wer es kreuzweise schickt, bekommt einen Fehler
 * statt stiller Ablage im falschen Feld — das war im alten Modul die Quelle für
 * eingehende Transporte mit Projekt, das nie jemand ausgewertet hat.
 */
export const createTransportSchema = z
  .object({ direction: transportDirectionSchema, ...transportFields })
  .refine((value) => value.direction !== 'OUTBOUND' || Boolean(value.projectId), {
    message: 'Ein ausgehender Transport braucht ein Projekt.',
    path: ['projectId'],
  })
  .refine((value) => value.direction !== 'OUTBOUND' || !value.purchaseOrderIds?.length, {
    message: 'Bestellungen gehören an einen eingehenden Transport.',
    path: ['purchaseOrderIds'],
  })
  .refine((value) => value.direction !== 'INBOUND' || !value.projectId, {
    message: 'Ein eingehender Transport hängt an Bestellungen, nicht an einem Projekt.',
    path: ['projectId'],
  });
export type CreateTransportInput = z.infer<typeof createTransportSchema>;

/** Kopfdaten ändern (Status läuft über einen eigenen Endpunkt, Richtung ist fix). */
export const updateTransportSchema = z
  .object(transportFields)
  .refine((value) => Object.keys(value).length > 0, { message: 'Mindestens ein Feld erforderlich.' });
export type UpdateTransportInput = z.infer<typeof updateTransportSchema>;

/** Status wechseln — erlaubte Ziele siehe {@link TRANSPORT_STATUS_TRANSITIONS}. */
export const setTransportStatusSchema = z.object({ status: transportStatusSchema });
export type SetTransportStatusInput = z.infer<typeof setTransportStatusSchema>;

// ── Antworten ────────────────────────────────────────────────────────────────

/** Verknüpfte Bestellung — Kurzinfo, damit die Liste ohne Nachladen lesbar ist. */
export const transportPurchaseOrderSummarySchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  supplierName: z.string(),
  status: z.string(),
});
export type TransportPurchaseOrderSummary = z.infer<typeof transportPurchaseOrderSummarySchema>;

export const transportResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  number: z.string(),
  status: transportStatusSchema,
  direction: transportDirectionSchema,
  /** Nur ausgehend. */
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  /** Nur eingehend — aufgelöst, damit Nummer und Lieferant direkt lesbar sind. */
  purchaseOrders: z.array(transportPurchaseOrderSummarySchema),
  /** Ziel bei ausgehend, Herkunft/Lieferant bei eingehend. */
  locationText: z.string().nullable(),
  /** Termin: Ankunft (eingehend) bzw. Abholung (ausgehend), Datum + Uhrzeit. */
  plannedAt: z.string().datetime().nullable(),
  /** Tatsächlich erledigt: abgefahren (ausgehend) bzw. angekommen (eingehend). */
  completedAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TransportResponse = z.infer<typeof transportResponseSchema>;

/**
 * Listen-Zeile. Im Minimalmodul identisch zur Vollantwort — es gibt keinen
 * Ladeplan mehr, der beim Auflisten teuer wäre. Der eigene Typ bleibt, damit
 * Liste und Detail später wieder auseinanderlaufen können, ohne alle Aufrufer
 * anzufassen.
 */
export const transportListItemSchema = transportResponseSchema;
export type TransportListItem = z.infer<typeof transportListItemSchema>;
